import {type PropsWithChildren, useCallback, useEffect, useMemo} from 'react'
import {ProjectsContext} from './ProjectsContext.tsx'
import {api} from '../../trpc/api.ts'
import {useArrayState} from '@mstuercke/react-utils/hooks'
import type {Project, ProjectId} from '@package/shared'
import type {AddProjectRequest, UpdateProjectRequest} from '@app/api-resolver/router'
import {showNotification} from '../../notifications/showNotification.ts'

export const ProjectsProvider = ({children}: PropsWithChildren) => {
  const {data, isLoading: loading} = api.projects.list.useQuery()
  const [projects, setProjects, {appendItem, replaceItem}] = useArrayState<Project>('id')
  useEffect(() => {
    if (!data) return
    setProjects(data)
  }, [data, setProjects])

  // add
  const {mutateAsync: addProject, isPending: addingProject} = api.projects.add.useMutation(
    showNotification('Project added successfully', 'Error while adding project'),
  )
  const handleAdd = useCallback(
    async (project: AddProjectRequest) => {
      const addedProject = await addProject(project)
      appendItem(addedProject)
      return addedProject
    },
    [addProject, appendItem],
  )

  // update
  const {mutateAsync: updateProject, isPending: updatingProject} = api.projects.update.useMutation(
    showNotification('Project updated successfully', 'Error while updating project'),
  )
  const handleUpdate = useCallback(
    async (project: UpdateProjectRequest) => {
      const updatedProject = await updateProject(project)
      replaceItem(updatedProject)
      return updatedProject
    },
    [replaceItem, updateProject],
  )

  // archive
  const {mutateAsync: archiveProject, isPending: archivingProject} = api.projects.archive.useMutation(
    showNotification('Project archived successfully', 'Error while archiving project'),
  )
  const handleArchive = useCallback(
    async (projectId: ProjectId) => {
      const archivedProject = await archiveProject({id: projectId})
      replaceItem(archivedProject)
      return archivedProject
    },
    [archiveProject, replaceItem],
  )

  // unarchived
  const {mutateAsync: unarchiveProject, isPending: unarchivingProject} = api.projects.unarchive.useMutation(
    showNotification('Project unarchived successfully', 'Error while unarchiving project'),
  )
  const handleUnarchive = useCallback(
    async (projectId: ProjectId) => {
      const unarchivedProject = await unarchiveProject({id: projectId})
      replaceItem(unarchivedProject)
      return unarchivedProject
    },
    [unarchiveProject, replaceItem],
  )

  const activeProjects = useMemo(() => projects.filter((project) => !project.archivedTime), [projects])
  const archivedProjects = useMemo(() => projects.filter((project) => !!project.archivedTime), [projects])

  return (
    <ProjectsContext.Provider
      value={{
        projects,
        activeProjects,
        archivedProjects,
        loading,

        addProject: handleAdd,
        updateProject: handleUpdate,
        archiveProject: handleArchive,
        unarchiveProject: handleUnarchive,
        saving: addingProject || updatingProject || archivingProject || unarchivingProject,
      }}
    >
      {children}
    </ProjectsContext.Provider>
  )
}
